// Service categories and data
// Categories align with the homepage Signature Treatments section.

export const serviceCategories = {
  aesthetics: {
    name: 'Aesthetics',
    description:
      'Advanced aesthetic treatments for skin rejuvenation, lifting, and enhancement.',
    services: [
      'xerf',
      'thulium-laser',
      'co2-laser',
      'ipl',
      'microneedling-skin',
      'microneedling-rf',
      'microdermabrasion',
      'injectables',
      'prp-prf-regenerative',
      'prp-hair-restoration',
      'facials',
    ],
  },
  precision: {
    name: 'Precision Health',
    description:
      'Data-driven, physician-led programmes for metabolism, hormones, longevity, and whole-body optimisation.',
    services: [
      'precision-weight-management',
      'hormone-optimization',
      'genetics-microbiome-biomarkers',
      'microbiome-deep-dive',
      'functional-medicine',
      'longevity-medicine',
      'visia-skin-analysis',
      'body-composition-analysis',
    ],
  },
  wellness: {
    name: 'Wellness Support',
    description:
      'Restorative wellness treatments designed to support energy, recovery, and radiance.',
    services: ['iv-therapy'],
  },
};

export const servicesData = {
  // ── AESTHETICS ─────────────────────────────────────────────────────────────
  xerf: {
    id: 'xerf',
    slug: 'xerf-structural-tightening',
    name: 'XERF Structural Tightening',
    tagline: "The world's first multifrequency monopolar RF treatment",
    badge: 'FDA Cleared',
    category: ['aesthetics'],
    image: '/xerf.jpg',
    shortDescription:
      'FDA-cleared radiofrequency energy delivered to the dermis for deep collagen and elastin remodeling — with little to no downtime and no numbing required.',
    fullDescription:
      'XERF is an FDA-cleared, non-invasive treatment that uses advanced radiofrequency energy to firm and rejuvenate the skin from within. By delivering controlled heat to the deeper layers of skin, XERF stimulates your body\'s natural collagen and elastin production — resulting in visibly tighter, smoother skin with minimal discomfort and no downtime. As your skin rebuilds its structural foundation over the following weeks, you\'ll experience gradual firming and lifting that continues to improve over time.',
    howItWorks: [
      'Uses dynamic temperature control to safely heat up to 42°C, targeting the dermis and SMAS layer',
      'Controlled heating causes immediate collagen contraction and activates new collagen production',
      'Wave Fit™ pulse technology controls energy delivery in real time',
      'Integrated cryogen cooling keeps the surface comfortable',
      'Never-Numb™ technology — no topical anesthetic required',
      'Suitable for all skin types and tones',
    ],
    benefits: [
      'Firms and tightens loose or sagging skin',
      'Stimulates natural collagen and elastin production',
      'Non-invasive treatment for face and neck',
      'Comfortable, with minimal discomfort',
      'Little to no downtime',
      'Progressive, natural-looking results',
    ],
    idealFor: 'Patients with loose or sagging skin · all skin types and tones. Not for patients with a pacemaker or implanted electronic device.',
    sessions: '1–2 initial sessions, then yearly maintenance as needed',
    downtime: 'Little to none — mild redness or warmth may occur for a few hours',
    results: 'Progressive firming and lifting over the following weeks',
  },

  'thulium-laser': {
    id: 'thulium-laser',
    slug: 'thulium-laser',
    name: 'Thulium Laser',
    tagline: 'Fractional skin resurfacing for tone, texture & renewal',
    badge: 'Fractional Laser',
    category: ['aesthetics'],
    image: '/thulium-laser.jpg',
    shortDescription:
      'Fractional 1927nm laser resurfacing that creates thousands of microscopic treatment zones to renew skin from within — with a faster recovery than traditional CO2.',
    fullDescription:
      "The Thulium Laser is a fractional skin resurfacing treatment that uses targeted laser energy to renew your skin from within. Operating at an optimal wavelength of 1927nm, it offers precise, customizable treatment depths. During your treatment, the Thulium laser creates thousands of microscopic treatment zones in the skin while leaving the surrounding tissue untouched. This triggers your body's natural healing response, stimulating fresh collagen production and cell turnover.",
    howItWorks: [
      'Creates thousands of microscopic treatment zones in the skin',
      'Surrounding tissue is left untouched for faster healing',
      'Triggers the body\'s natural healing response',
      'Stimulates fresh collagen production and cell turnover',
      'Customisable depth and intensity for personalised results',
    ],
    benefits: [
      'Reduces fine lines, wrinkles, age spots and acne scars',
      'Addresses uneven pigmentation and sun damage',
      'Smooths rough or uneven skin texture',
      'Faster recovery than traditional CO2 lasers',
      'Compatible with a broader range of skin tones',
      'Long-lasting results that continue improving as collagen remodels',
    ],
    idealFor: 'A wide range of skin types and concerns — fine lines, pigmentation, sun damage, texture',
    sessions: 'One treatment or a series of 2–3 sessions spaced several months apart',
    downtime: 'Redness, warmth and mild swelling for 3–7 days',
    results: 'Progressive improvement as collagen continues to remodel',
  },

  'co2-laser': {
    id: 'co2-laser',
    slug: 'co2-laser-resurfacing',
    name: 'CO2 Laser Resurfacing',
    tagline: 'Powerful resurfacing for deep wrinkles, scarring & sun damage',
    badge: 'Advanced Resurfacing',
    category: ['aesthetics'],
    image: '/co2-laser.jpg',
    shortDescription:
      'One of the most powerful resurfacing treatments available — fractional CO2 vaporizes damaged layers and triggers robust collagen regeneration.',
    fullDescription:
      'CO2 Laser Resurfacing is one of the most powerful treatments available for significant signs of aging, sun damage, deep wrinkles, and scarring. This proven technology delivers transformative results by precisely removing damaged skin layers and stimulating robust collagen regeneration for dramatically smoother, more youthful-looking skin. Modern fractional CO2 lasers create tiny treatment zones surrounded by healthy tissue, which speeds up healing while maintaining powerful results.',
    howItWorks: [
      'Concentrated CO2 light energy gently vaporizes damaged outer skin layers',
      'Heat penetrates deeper to trigger collagen remodeling and skin tightening',
      'Fractional pattern creates tiny treatment zones surrounded by healthy tissue',
      'Healing is accelerated while powerful results are maintained',
      'New collagen and elastin produce firmer, smoother skin over the following weeks',
    ],
    benefits: [
      'Dramatically reduces deep wrinkles and lines',
      'Improves acne scars and surgical scars',
      'Eliminates sun damage and age spots',
      'Noticeable skin tightening and lifting',
      'Refines rough or uneven texture',
      'Long-lasting results, often from a single treatment',
    ],
    idealFor: 'Patients with significant signs of aging, sun damage, deep wrinkles and scarring',
    sessions: 'One treatment or a series of 2–3 sessions spaced several months apart',
    downtime: 'Redness, swelling and crusting for 5–10 days',
    results: 'Dramatic, long-lasting improvement as collagen remodels',
  },

  ipl: {
    id: 'ipl',
    slug: 'ipl-photofacial',
    name: 'IPL Photofacial',
    tagline: 'Intense Pulsed Light for pigmentation, redness & clarity',
    badge: 'Light Therapy',
    category: ['aesthetics'],
    image:
      'https://images.pexels.com/photos/3993454/pexels-photo-3993454.jpeg?w=1920&q=80',
    shortDescription:
      'Gentle, non-invasive multi-wavelength light therapy that targets discoloration and vascular concerns for clearer, more even-toned skin.',
    fullDescription:
      'IPL (Intense Pulsed Light) is a gentle, non-invasive light-based treatment that targets discoloration and vascular concerns to reveal clearer, more even-toned skin with minimal downtime. Unlike lasers that use a single wavelength, IPL delivers multiple wavelengths of light that can be tuned to target specific concerns — brown spots, redness, broken capillaries, and uneven texture. The light energy is absorbed by pigmented areas and blood vessels, converting to heat that breaks down unwanted pigmentation and collapses tiny visible vessels. Your body then naturally clears these treated cells over the following weeks, revealing clearer, more uniform skin. IPL also stimulates collagen production for improved overall skin quality.',
    howItWorks: [
      'Multiple wavelengths of light are tuned to target specific concerns',
      'Light energy is absorbed by pigmented areas and blood vessels',
      'Energy converts to heat that breaks down pigmentation and collapses tiny visible vessels',
      'The body naturally clears treated cells over the following weeks',
      'Collagen production is stimulated, improving overall skin quality',
    ],
    benefits: [
      'Reduces sunspots, age spots and freckles',
      'Minimizes redness and rosacea',
      'Fades broken capillaries and spider veins',
      'Evens overall skin tone and texture',
      'Quick sessions of 15–30 minutes',
      'Safe for face, neck, chest and hands',
    ],
    idealFor: 'Patients with discoloration, vascular concerns or sun damage seeking a more balanced complexion',
    sessions: 'Series of 3–5 treatments spaced 3–4 weeks apart',
    downtime: 'Minimal — mild redness and warmth for a few hours',
    results: 'Progressive clarity and tone improvement over the full series',
  },

  'microneedling-skin': {
    id: 'microneedling-skin',
    slug: 'skin-microneedling',
    name: 'Skin Microneedling',
    tagline: 'Collagen induction therapy for texture, tone & firmness',
    badge: 'Gold Standard',
    category: ['aesthetics'],
    image: '/skin-microneedling.jpg',
    shortDescription:
      'A minimally invasive treatment that uses fine needles to create controlled micro-channels, stimulating natural collagen and elastin production.',
    fullDescription:
      "Skin Microneedling is a minimally invasive treatment that uses fine needles to create controlled micro-channels in the skin, stimulating your body's natural collagen and elastin production. This gentle yet effective procedure improves skin texture, tone, and firmness — without heat or energy, making it suitable for a wide range of skin types. The micro-channels also allow for deeper penetration of serums and active ingredients, maximizing their effectiveness. Can be combined with regenerative treatments like PRP for enhanced results.",
    howItWorks: [
      'Fine needles create precise micro-injuries in the skin\'s surface',
      'The body\'s natural repair response is triggered',
      'Fresh collagen and elastin are produced from within',
      'Micro-channels allow deeper penetration of serums and actives',
      'Can be combined with PRP for enhanced regenerative results',
    ],
    benefits: [
      'Refines skin texture and evens tone',
      'Reduces fine lines, acne scars and enlarged pores',
      'Stimulates natural collagen and elastin production',
      'Enhances absorption of skincare products',
      'Safe for most skin types and tones',
      'Can be performed on face, neck and décolletage',
    ],
    idealFor: 'Concerns with skin texture, tone, firmness, fine lines, acne scars or enlarged pores',
    sessions: 'Series of 3–6 treatments spaced 4 weeks apart',
    downtime: 'Redness and mild swelling for 24–72 hours',
    results: 'Progressive, natural-looking improvement over the series',
  },

  'microneedling-rf': {
    id: 'microneedling-rf',
    slug: 'rf-microneedling',
    name: 'RF Microneedling',
    tagline: 'Microneedling + radiofrequency for deep dermal remodeling',
    badge: 'Advanced',
    category: ['aesthetics'],
    image:
      'https://images.pexels.com/photos/3762871/pexels-photo-3762871.jpeg?w=1920&q=80',
    shortDescription:
      'Combines the precision of microneedling with the tissue-remodeling power of radiofrequency to address laxity, fine lines, texture and scarring.',
    fullDescription:
      "RF Microneedling combines the precision of microneedling with the tissue-remodeling power of radiofrequency energy. Together, these two technologies work beneath the surface to address skin laxity, fine lines, uneven texture, and scarring — promoting natural healing and renewal from the inside out. Ultra-fine needles create precise micro-channels in the skin while simultaneously delivering radiofrequency energy into the deeper dermal layers. This combination triggers your body's regenerative processes, encouraging fresh collagen formation for skin that looks refreshed, firmer, and more luminous.",
    howItWorks: [
      'Ultra-fine needles create precise micro-channels in the skin',
      'Radiofrequency energy is simultaneously delivered into deeper dermal layers',
      'The dual stimulus triggers the body\'s regenerative processes',
      'Fresh collagen formation is encouraged at depth',
      'Skin appearance refines progressively across the treatment series',
    ],
    benefits: [
      'Diminishes visible signs of aging and acne scarring',
      'Refines skin texture for a smoother, more even appearance',
      'Provides natural lifting and firming effects',
      'Minimal recovery time with meaningful, lasting results',
      'Activates the skin\'s own collagen response',
    ],
    idealFor: 'Skin laxity, fine lines, uneven texture and scarring',
    sessions: '3 sessions spaced 4–6 weeks apart',
    downtime: 'Minimal — mild redness and swelling for 1–3 days',
    results: 'Progressive remodeling over the months following the series',
  },

  microdermabrasion: {
    id: 'microdermabrasion',
    slug: 'microdermabrasion',
    name: 'Microdermabrasion',
    tagline: 'Gentle exfoliation for instant glow and refined texture',
    badge: 'Express Treatment',
    category: ['aesthetics'],
    image: '/microdermabrasion.jpg',
    shortDescription:
      'A gentle, non-invasive exfoliation that buffs away dull, dead skin and reveals a brighter, smoother surface — with no downtime.',
    fullDescription:
      "Microdermabrasion is a gentle, non-invasive exfoliation treatment that revitalizes dull, tired skin by removing the outermost layer of dead cells and stimulating natural renewal. A specialized handpiece delivers gentle abrasive action across the skin's surface — either through fine crystal exfoliation or a diamond-tipped wand — buffing away the dull outer layer. Simultaneously, a light vacuum suction removes exfoliated cells, stimulates blood flow, and encourages circulation.",
    howItWorks: [
      'Fine crystal or diamond-tipped wand gently exfoliates the surface',
      'Light vacuum suction removes exfoliated cells',
      'Blood flow and circulation are stimulated',
      'Pores are unclogged and texture is refined',
      'Collagen and elastin production is gently encouraged',
    ],
    benefits: [
      'Improves overall skin texture and smoothness',
      'Reduces the appearance of fine lines and wrinkles',
      'Diminishes mild acne scars and skin discoloration',
      'Minimizes enlarged pores',
      'Enhances absorption of skincare products',
      'Quick sessions of 30–45 minutes — no downtime',
    ],
    idealFor: 'Dull, tired skin, fine lines, mild acne scars, discoloration or enlarged pores',
    sessions: '4–6 sessions spaced 2–4 weeks apart; monthly maintenance recommended',
    downtime: 'None — skin may appear pink for a few hours',
    results: 'Immediate glow with cumulative improvement over a series',
  },

  injectables: {
    id: 'injectables',
    slug: 'injectables-fillers',
    name: 'Injectables & Fillers',
    tagline: 'Neuromodulators, dermal fillers & biostimulators — physician-led',
    badge: 'FDA Approved',
    category: ['aesthetics'],
    image: '/injectables-fillers.jpg',
    shortDescription:
      'FDA-approved injectables that gently relax expression lines and restore facial volume — administered exclusively by our board-certified medical team.',
    fullDescription:
      "Our cosmetic injectables programme covers FDA-approved neuromodulators and dermal fillers, administered exclusively by our board-certified medical team. Neuromodulators are purified proteins that temporarily pause communication between specific nerves and the muscles they control — relaxing the underlying muscles that create dynamic wrinkles on the forehead, between the brows, and around the eyes. Dermal fillers restore volume, soften facial lines, and enhance natural contours: hyaluronic acid fillers (gel-based) immediately restore volume and create structural support, while biostimulatory fillers encourage your body to produce its own collagen over time for natural-looking, long-lasting improvements.",
    treatments: [
      'Neuromodulators: Botox®, Dysport®, Xeomin® and Letybo® (Letibotulinum)',
      'Hyaluronic acid fillers: Restylane® and Juvéderm®',
      'Collagen biostimulators: Sculptra® and Radiesse®',
      'Lip augmentation',
      'Cheek and chin enhancement',
      'Jawline definition',
      'Under-eye rejuvenation',
    ],
    benefits: [
      'Reduces expression lines and dynamic wrinkles',
      'Helps prevent future wrinkle formation with consistent treatment',
      'Restores facial volume for a more youthful, rested appearance',
      'Softens lines, folds and wrinkles',
      'Enhances cheeks, chin, lips and jawline definition',
      'Personalised to complement your unique facial structure',
    ],
    idealFor: 'Anyone looking to reduce expression lines, soften folds, or restore volume to hollowed areas',
    sessions:
      'Neuromodulator effects last 3–4 months · filler results visible immediately with final result at 2 weeks, typically lasting 6–18 months',
    downtime: 'Minimal — mild redness, swelling or bruising at injection sites, resolves quickly',
    results: 'Neuromodulators: 3–7 days · Fillers: immediate, with final result at 2 weeks',
  },

  'prp-prf-regenerative': {
    id: 'prp-prf-regenerative',
    slug: 'prp-prf-regenerative-therapy',
    name: 'PRP & PRF Regenerative Therapy',
    tagline: 'Your own growth factors for skin renewal & collagen',
    badge: 'Regenerative',
    category: ['aesthetics'],
    image: '/prp-prf-regenerative.jpg',
    shortDescription:
      'Advanced regenerative therapies that use the healing power already present in your own blood to rejuvenate skin, stimulate collagen, and support recovery.',
    fullDescription:
      "PRP and PRF are advanced regenerative therapies that use the healing power already present in your own blood to rejuvenate skin and promote collagen synthesis. Both are derived from a small blood draw, which is then placed in a centrifuge to concentrate the growth factors naturally found in your blood. This enriched solution is carefully delivered either by injection or combined with microneedling to the face, scalp or under-eye area to stimulate cellular renewal and collagen production. PRP (Platelet-Rich Plasma) delivers an immediate release of growth factors and is frequently combined with laser treatments or microneedling to enhance and accelerate results. PRF (Platelet-Rich Fibrin) offers a slower, sustained release of growth factors for more prolonged collagen stimulation — particularly effective for treating under-eye hollowness and dark circles.",
    treatments: [
      'PRP injections for facial rejuvenation',
      'PRP combined with microneedling (collagen induction)',
      'PRF for under-eye hollowness and dark circles',
      'PRP alongside laser treatments to accelerate recovery',
      'Targeted scalp PRP for hair density support',
    ],
    benefits: [
      'Enhances skin texture, tone and elasticity',
      'Diminishes fine lines and under-eye hollowing',
      'Promotes natural collagen and elastin production',
      'Supports hair regrowth and density in thinning areas',
      'Minimal downtime and very low risk of adverse reactions',
      'Uses your body\'s own biology — no foreign substances',
    ],
    idealFor: 'Skin rejuvenation, fine lines, under-eye hollowing, scarring, and early-stage hair thinning',
    sessions: 'Series of 3–4 sessions spaced 4–6 weeks apart',
    downtime: 'Minimal — mild swelling, redness or bruising, temporary',
    results: 'Cumulative skin and collagen improvement over the series',
  },

  'prp-hair-restoration': {
    id: 'prp-hair-restoration',
    slug: 'prp-hair-restoration',
    name: 'PRP Hair Restoration',
    tagline: 'Non-surgical hair restoration with your own growth factors',
    badge: 'Hair Restoration',
    category: ['aesthetics'],
    image: '/prp-hair-restoration.jpg',
    shortDescription:
      'A non-surgical hair restoration option that uses concentrated growth factors from your own blood to reactivate dormant follicles and encourage natural regrowth.',
    fullDescription:
      "PRP Hair Restoration uses concentrated growth factors from your own blood to reactivate dormant hair follicles, strengthen the scalp, and encourage natural regrowth. This non-surgical treatment is a safe, effective option for both men and women experiencing hair thinning or loss. A small blood sample is drawn and placed in a centrifuge, which separates and concentrates the plasma rich in regenerative proteins. This solution is then carefully injected into areas of thinning or loss. The growth factors work to awaken dormant follicles, stimulate cellular activity, and improve hair density through your body's own natural processes.",
    howItWorks: [
      'A small blood sample is drawn and centrifuged to concentrate growth factors',
      'The plasma rich in regenerative proteins is prepared for injection',
      'Carefully injected into areas of thinning or loss on the scalp',
      'Growth factors awaken dormant follicles and stimulate cellular activity',
      'Hair density improves through the body\'s natural processes',
    ],
    benefits: [
      'Promotes natural hair regrowth and follicle reactivation',
      'Strengthens hair shafts and increases overall density',
      'Non-surgical and minimally invasive',
      'Uses your body\'s own biology — no foreign substances',
      'Reduces active hair shedding',
    ],
    idealFor: 'Men and women experiencing hair thinning or loss',
    sessions: 'Initial series of 3–4 sessions every 4 weeks, then maintenance every 4–6 months',
    downtime: 'Mild tenderness or swelling at injection sites — temporary',
    results: 'Gradual density improvement over the course of treatment',
  },

  facials: {
    id: 'facials',
    slug: 'advanced-facial-red-light-therapy',
    name: 'Advanced Facial / Red Light Therapy',
    tagline: 'HydraFacial, red light therapy & customised skincare',
    badge: 'Advanced',
    category: ['aesthetics'],
    image: '/hydrafacial.jpg',
    shortDescription:
      'Multi-step HydraFacial using the IDS Hydro Plus system paired with customisable red light therapy — supporting acne, fine lines, body contouring, recovery, and more.',
    fullDescription:
      'Our advanced facial menu goes beyond traditional spa treatments. The HydraFacial — performed with the IDS Hydro Plus system — follows a three-phase approach using specialised serums and tips: Cleanse & Exfoliate (gentle resurfacing removes dead skin cells to reveal a fresh layer), Extract & Hydrate (patented vortex suction painlessly clears congestion while infusing intensely hydrating serums), and Fuse & Protect (nourishing antioxidant, peptide and hyaluronic acid serums are delivered deep into the skin via vortex-fusion technology). Each session is paired with customisable red light therapy. Red light settings can be tailored for: acne, fine lines and wrinkles, body contouring, recovery from aches and pains, scalp and hair support, and general skin radiance.',
    treatments: [
      'HydraFacial — IDS Hydro Plus (3-phase: Cleanse & Exfoliate · Extract & Hydrate · Fuse & Protect)',
      'Customisable booster serums (antioxidants, peptides, hyaluronic acid)',
      'Chemical peels (varying strengths)',
      'Red light therapy — customisable settings',
      'Dermaplaning',
      'Oxygen infusion',
    ],
    benefits: [
      'Deeply cleanses and exfoliates without irritation',
      'Removes blackheads and clears congested pores',
      'Hydrates and plumps the skin',
      'Reduces appearance of fine lines and wrinkles',
      'Body contouring & recovery support via red light',
      'Suitable for all skin types, including sensitive skin',
    ],
    idealFor: 'All skin types — including sensitive skin — seeking customised facial care, red light therapy, or both',
    sessions: 'Monthly or every 4–6 weeks for optimal results',
    downtime: 'None — skin may appear slightly pink for 1–2 hours',
    results: 'Instant glow with cumulative improvement over a series',
  },

  // ── PRECISION HEALTH ───────────────────────────────────────────────────────
  'precision-weight-management': {
    id: 'precision-weight-management',
    slug: 'precision-weight-management',
    name: 'Precision Weight Management',
    tagline: 'GLP-1 therapy + metabolic testing for sustainable weight loss',
    badge: 'FDA Approved',
    category: ['precision'],
    image:
      'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1920&q=80',
    shortDescription:
      'FDA-approved GLP-1 prescription therapy paired with metabolic and body-composition testing — designed for significant, sustainable weight loss with physician oversight.',
    fullDescription:
      "GLP-1 (Glucagon-Like Peptide-1) medications are injectable prescription treatments originally developed for type 2 diabetes management and now FDA-approved for chronic weight management. By working with your body's natural hunger and metabolism signals, GLP-1 therapy helps reduce appetite, improve blood sugar regulation, and promote gradual, meaningful weight reduction when combined with healthy lifestyle changes. These medications slow the rate at which food leaves your stomach, signal the brain's appetite centers to reduce hunger and cravings, and help regulate blood sugar by stimulating insulin release when needed. We pair therapy with metabolic and body-composition testing so your plan protects muscle, hormones, and long-term vitality — not just the number on the scale.",
    howItWorks: [
      'Comprehensive metabolic + body-composition baseline assessment',
      'Tailored GLP-1 medication protocol (where clinically appropriate)',
      'Weekly self-administered injection with gradual dose escalation to minimise side effects',
      'Personalised nutrition and movement strategy',
      'Regular follow-up appointments and biomarker monitoring',
    ],
    benefits: [
      'Significant and sustained weight loss (average 10–15% of body weight)',
      'Reduced appetite and food cravings',
      'Improved blood sugar regulation',
      'Better portion control and satiety',
      'May reduce risk factors for heart disease and diabetes',
      'Supports long-term maintenance when combined with lifestyle changes',
    ],
    idealFor:
      'Adults seeking effective, sustainable, physician-supervised weight management. Not for pregnant or breastfeeding individuals.',
    sessions: 'Weekly injection · most patients continue for 12 months or more',
    downtime: 'Minimal — early nausea, reduced appetite, or digestive changes typically improve over time',
    results:
      'Progressive, sustainable weight loss over the course of treatment',
  },

  'hormone-optimization': {
    id: 'hormone-optimization',
    slug: 'hormone-optimization',
    name: 'Hormone Optimization',
    tagline: 'Bioidentical hormone therapy for energy, mood & vitality',
    badge: 'Precision Health',
    category: ['precision'],
    image:
      'https://images.pexels.com/photos/8460346/pexels-photo-8460346.jpeg?w=1920&q=80',
    shortDescription:
      'Comprehensive hormone evaluation and personalised optimization for energy, mood, libido, body composition, and long-term vitality.',
    fullDescription:
      'Our hormone optimization programme uses comprehensive lab testing and bioidentical hormone therapy to restore the hormonal balance that underpins energy, mood, sleep, libido, body composition, and long-term quality of life — for women and men across every life stage.',
    howItWorks: [
      'Comprehensive hormone, thyroid, and adrenal panel',
      'Symptom and lifestyle assessment',
      'Personalised bioidentical hormone protocol',
      'Targeted nutrient and supplement support',
      'Ongoing monitoring and dose refinement',
    ],
    benefits: [
      'Improved energy and stamina',
      'Better sleep and mood',
      'Restored libido',
      'Healthier body composition',
      'Sharper cognition',
      'Long-term vitality',
    ],
    idealFor:
      'Women and men experiencing perimenopause, menopause, andropause, fatigue, or low vitality',
    sessions: 'Initial consultation + quarterly follow-ups',
    downtime: 'None',
    results: 'Most patients report meaningful changes within 6–12 weeks',
  },

  'genetics-microbiome-biomarkers': {
    id: 'genetics-microbiome-biomarkers',
    slug: 'genetics-microbiome-biomarkers',
    name: 'Genetics, Microbiome & Biomarkers',
    tagline: 'Multi-omics analysis for the deepest biological snapshot',
    badge: 'Precision Health',
    category: ['precision'],
    image: '/microbiome-genetics.jpg',
    shortDescription:
      'Genetic SNP analysis, Viome microbiome RNA profiling, and 160+ blood biomarkers — together they reveal exactly what your body needs.',
    fullDescription:
      'This is the most comprehensive precision health workup we offer. Combining genetic SNP mapping, Viome microbiome RNA sequencing, and 160+ blood biomarkers, we build a complete biological profile and translate it into a clear, personalised action plan.',
    includes: [
      'Genetic SNP analysis (methylation, detox, hormones)',
      'Viome microbiome RNA analysis (gut, oral)',
      'Function Health 160+ biomarker panel',
      'Inflammation and metabolic pathway mapping',
      'Personalised nutrition and supplement protocol',
      'Multi-session physician consultations and follow-up',
    ],
    benefits: [
      'Understand your unique biology',
      'Identify hidden inflammation and deficiencies',
      'Personalised nutrition and supplementation',
      'Optimise gut and metabolic health',
      'Reduce chronic symptoms',
      'Build a precise long-term protocol',
    ],
    idealFor:
      'Anyone serious about understanding their biology and optimising it precisely',
    sessions: 'Multi-session programme (testing + interpretation + follow-up)',
    downtime: 'None',
    results: 'Complete biological roadmap with a personalised protocol',
    price: '$5,000',
    stripeUrl: 'https://buy.stripe.com/aFa7sK5Yme1Q3qK86RfjG0i',
    ctaLabel: 'Purchase Programme',
  },

  'microbiome-deep-dive': {
    id: 'microbiome-deep-dive',
    slug: 'microbiome-deep-dive',
    name: 'Microbiome Deep Dive',
    tagline: 'Viome PRO testing — gut, oral, and gene expression, decoded',
    badge: 'Precision Health',
    category: ['precision'],
    image: 'https://images.pexels.com/photos/25626515/pexels-photo-25626515.jpeg?w=1600&q=80',
    shortDescription:
      'The complete Viome PRO testing kit — three tests (oral microbiome, gut microbiome, and blood-based gene expression) — plus a private consultation with our microbiome expert to translate the results into a personalised protocol.',
    fullDescription:
      "Your microbiome is a living ecosystem that influences energy, immunity, hormones, skin, mood, and metabolic health. The Viome PRO platform reads not just which microbes are present, but which genes are actively being expressed — so we see what your biology is actually doing right now, not just its potential. Paired with blood-based gene expression testing, this is the deepest microbial and biological snapshot available outside of a research lab. Our microbiome expert walks you through every insight and builds a clear, food-first protocol tailored to your findings.",
    includes: [
      'Viome PRO Gut Intelligence™ Test (stool sample)',
      'Viome Oral Health Intelligence™ Test (saliva sample)',
      'Viome Health Intelligence™ Blood Test (gene expression + biomarkers)',
      'At-home kit shipped directly to you with clear instructions',
      'Lab processing and full Viome results report',
      'Private results-review consultation with our microbiome expert',
      'Personalised food, supplement, and lifestyle protocol based on your data',
    ],
    benefits: [
      'See exactly which foods heal or inflame your body',
      'Identify hidden gut and oral dysbiosis',
      'Understand cellular gene expression, not just genetics',
      'Personalised, food-first protocol',
      'Address root drivers of fatigue, bloating, and skin issues',
      'Track progress with objective retesting',
    ],
    idealFor:
      'Anyone with gut symptoms, chronic fatigue, skin flares, or a strong desire to optimise health from the microbial level — no clinic visit required to start.',
    sessions: 'One at-home kit (3 samples) + one results-review consultation',
    downtime: 'None — samples collected at home on your schedule',
    results:
      'Full Viome PRO report + a personalised food, supplement, and lifestyle protocol',
    price: '$1,500',
    stripeUrl: 'https://buy.stripe.com/fZu9ASbiG0b0e5ocn7fjG0h',
    ctaLabel: 'Purchase Kit',
    externalReference: {
      label: 'Learn more about Viome PRO',
      url: 'https://www.viomepro.com/',
    },
  },

  'functional-medicine': {
    id: 'functional-medicine',
    slug: 'functional-medicine',
    name: 'Functional Medicine',
    tagline: 'Root-cause care for chronic symptoms and inflammation',
    badge: 'Precision Health',
    category: ['precision'],
    image: '/functional-medicine.jpg',
    shortDescription:
      'Whole-person, root-cause medicine for chronic symptoms, fatigue, gut issues, autoimmunity, and inflammation.',
    fullDescription:
      'Functional medicine looks beyond symptoms to address the underlying drivers of chronic disease. We use detailed history, advanced lab testing, and an integrative, evidence-based approach to design personalised protocols that resolve issues at their source.',
    howItWorks: [
      'In-depth health history and symptom mapping',
      'Targeted functional and advanced lab testing',
      'Identify root causes — gut, inflammation, toxins, hormones',
      'Personalised nutrition, lifestyle, and supplement plan',
      'Ongoing follow-up and protocol refinement',
    ],
    benefits: [
      'Address chronic symptoms at the source',
      'Improve gut health',
      'Reduce systemic inflammation',
      'Support detoxification pathways',
      'Personalised nutrition strategy',
      'Sustainable, long-term results',
    ],
    idealFor:
      'Patients with chronic fatigue, gut issues, autoimmunity, hormonal imbalances, or unexplained symptoms',
    sessions: 'Initial deep-dive + ongoing follow-ups',
    downtime: 'None',
    results: 'Progressive resolution of symptoms over 3–6 months',
  },

  'longevity-medicine': {
    id: 'longevity-medicine',
    slug: 'longevity-medicine',
    name: 'Longevity Medicine',
    tagline: 'Quality-of-life optimisation, designed for the long game',
    badge: 'Precision Health',
    category: ['precision'],
    image:
      'https://images.pexels.com/photos/8939960/pexels-photo-8939960.jpeg?w=1920&q=80',
    shortDescription:
      'Concierge-level longevity programme integrating biological age testing, advanced biomarkers, and targeted interventions to improve long-term quality of life.',
    fullDescription:
      "Our longevity medicine programme is designed to improve long-term quality of life — supporting the years you actually live well. We combine biological age testing, advanced cardiometabolic and longevity biomarkers, and targeted clinical interventions into a single, physician-led plan focused on day-to-day vitality, resilience, and how you feel as you age.",
    includes: [
      'Biological age testing',
      'Advanced cardiometabolic biomarker panel',
      'Longevity-focused hormone and nutrient assessment',
      'Targeted interventions (nutrition, supplements, peptides where appropriate)',
      'Ongoing physician coaching',
      'Periodic re-testing and protocol refinement',
    ],
    benefits: [
      'Slow biological ageing',
      'Improve metabolic resilience',
      'Reduce long-term disease risk',
      'Better energy and cognition',
      'Personalised longevity protocol',
      'Long-term physician partnership',
    ],
    idealFor:
      'Adults who want a structured, physician-led plan focused on quality of life',
    sessions:
      'Annual programme with quarterly check-ins and re-testing',
    downtime: 'None',
    results: 'Measurable improvements in biomarkers and biological age over time',
  },

  'visia-skin-analysis': {
    id: 'visia-skin-analysis',
    slug: 'visia-skin-analysis',
    name: 'VISIA Skin Analysis',
    tagline: 'Multi-spectral imaging that reveals what the eye can\'t see',
    badge: 'Precision Imaging',
    category: ['precision'],
    image: '/visia-skin-analysis.jpg',
    shortDescription:
      'Advanced digital imaging that maps surface and subsurface skin conditions — giving us objective data to design your treatment plan and track progress.',
    fullDescription:
      "VISIA Skin Analysis (by Canfield) is an advanced digital imaging system that reveals what the naked eye simply can't see. Using multi-spectral photography and intelligent software, VISIA maps both surface and subsurface skin conditions — giving us the precise, objective data needed to design your most effective treatment plan and track your skin's progress over time. During your session, the system captures high-resolution photographs from multiple angles using three different light modes: standard lighting for surface texture and visible concerns, cross-polarized light for subsurface pigmentation and vascular issues, and UV photography to expose hidden sun damage, bacterial presence, and deeper pigmentation. The software analyses your images across eight key parameters and compares them to others of a similar age and skin type, including a personalised TruSkin Age.",
    includes: [
      'Spots — visible pigmentation and freckles',
      'Pores — size and distribution',
      'Wrinkles — depth and density',
      'Texture — surface smoothness and irregularities',
      'UV spots — hidden sun damage not yet visible on the surface',
      'Brown spots — deeper pigmentation concerns',
      'Red areas — vascular conditions and inflammation',
      'Porphyrins — bacterial presence linked to acne',
    ],
    benefits: [
      'Reveals hidden skin damage not yet visible to the naked eye',
      'Identifies sun damage, pigmentation and premature aging',
      'Detects underlying vascular conditions and inflammation',
      'Creates an objective baseline for your treatment plan',
      'Tracks progress and treatment effectiveness over time',
      'Generates a personalised TruSkin Age',
    ],
    idealFor: 'Anyone interested in understanding their skin health and designing a personalised treatment plan',
    sessions: 'Baseline imaging + follow-up scans every 3–6 months',
    downtime: 'None — non-invasive, takes only minutes',
    results: 'Objective, comparable imaging to track skin improvement over time',
  },

  'body-composition-analysis': {
    id: 'body-composition-analysis',
    slug: 'body-composition-analysis',
    name: 'Body Composition Analysis',
    tagline: 'Bioelectrical impedance for muscle, fat, hydration & metabolism',
    badge: 'Precision Imaging',
    category: ['precision'],
    image: '/body-composition.jpg',
    shortDescription:
      'A safe, painless bioelectrical impedance assessment that measures muscle mass, body fat %, hydration, visceral fat, and metabolic rate.',
    fullDescription:
      "Body Composition Analysis goes beyond what a standard scale can tell you. Using bioelectrical impedance technology, this assessment measures key health metrics — including muscle mass, body fat percentage, hydration levels, and metabolic rate — giving you the detailed, actionable insights needed to optimise your health and wellness goals. A safe, painless electrical current passes through your body via the scale or hand electrodes. Because different tissues — muscle, fat, bone, water — conduct electricity at different rates, the device measures this resistance and uses sophisticated algorithms to calculate a full breakdown of your body's composition. The entire process takes just a few minutes.",
    includes: [
      'Body fat percentage — not just total weight',
      'Lean muscle mass — tracked over time',
      'Visceral fat — the type that surrounds internal organs',
      'Hydration status and fluid balance',
      'Basal metabolic rate (calories burned at rest)',
      'Segmental analysis for arms, legs and trunk',
    ],
    benefits: [
      'Detailed picture of body composition, not just weight',
      'Tracks muscle gain or loss over time',
      'Assesses visceral fat — a key metabolic risk marker',
      'Evaluates hydration and fluid balance',
      'Quantifies your basal metabolic rate',
      'Quick, non-invasive, completely painless',
    ],
    idealFor: 'Anyone optimising health or weight goals. Not for individuals with a pacemaker or who are pregnant.',
    sessions: 'Baseline + periodic re-tests aligned with your treatment plan',
    downtime: 'None — takes just a few minutes',
    results: 'Objective body-composition trend data to guide your programme',
  },

  // ── WELLNESS SUPPORT ──────────────────────────────────────────────────────
  'iv-therapy': {
    id: 'iv-therapy',
    slug: 'iv-therapy',
    name: 'Akira Infusion Therapy',
    tagline: 'Intravenous nutrient drips for energy, recovery & radiance',
    badge: 'Wellness',
    category: ['wellness'],
    image: '/iv-therapy.jpg',
    shortDescription:
      'Targeted IV vitamin, mineral, and antioxidant infusions delivered directly to your bloodstream for maximum absorption.',
    fullDescription:
      'Our IV therapy menu delivers targeted blends of vitamins, minerals, antioxidants, and amino acids directly into your bloodstream — bypassing the gut entirely for near-100% absorption. Choose from hydration, recovery, immunity, glow, and NAD+ blends.',
    treatments: [
      'Myers Cocktail (foundational vitamins & minerals)',
      'Glutathione + antioxidant blend (skin radiance)',
      'NAD+ boost (cellular energy & longevity)',
      'Hydration & recovery drip',
      'Immune support blend',
      'Custom physician-formulated blends',
    ],
    benefits: [
      'Rapid hydration',
      'Boosted energy',
      'Faster recovery',
      'Stronger immunity',
      'Brighter skin',
      'Targeted nutrient repletion',
    ],
    idealFor:
      'Athletes, frequent travellers, busy professionals, or anyone seeking targeted nutrient support',
    sessions: 'Single drip or recurring series',
    downtime: 'None',
    results: 'Most patients feel the effects within hours',
  },
};

// Get services by category
export const getServicesByCategory = (category) => {
  return (
    serviceCategories[category]?.services
      .map((slug) => servicesData[slug])
      .filter(Boolean) || []
  );
};

// Get all services in a flat array (ordered by category)
export const getAllServices = () => {
  const ordered = [];
  Object.values(serviceCategories).forEach((cat) => {
    cat.services.forEach((key) => {
      if (servicesData[key]) ordered.push(servicesData[key]);
    });
  });
  return ordered;
};

// Get service by slug
export const getServiceBySlug = (slug) => {
  return Object.values(servicesData).find(
    (service) => service.slug === slug
  );
};
